import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";
import SocialStrip from "@/components/SocialStrip";
import DropBanner from "@/components/DropBanner";
import HolidayDrop from "@/components/HolidayDrop";
import { api } from "@/lib/api";

const COLLECTION_TILES = [
  { name: "CLASSIC DEJON RICHH", status: "DROPS OCT 1", img: "/products/back.png", to: "/product/classic-dejon-richh-tee", live: true },
  { name: "DR1 GRAFFITI", status: "COMING SOON", img: "/products/detail.png", live: false },
  { name: "HERO COLLECTION", status: "COMING SOON", img: "/products/hero-front.png", live: false },
  { name: "CROCHET COLLECTION", status: "COMING SOON", img: "/products/collar.png", live: false },
];

const VALUES = ["PREMIUM COTTON", "RELAXED STREETWEAR FIT", "VINTAGE-INSPIRED WASH", "SIGNATURE DR ARTWORK"];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const [products, setProducts] = useState(null);
  useEffect(() => {
    api.getProducts().then(setProducts).catch(() => {});
  }, []);

  const classic = products?.find((p) => p.slug === "classic-dejon-richh-tee");
  const backstabber = products?.find((p) => p.slug === "limited-edition-pink-backstabber-tee");
  const classicDropped = !!classic && (
    classic.status !== "coming_soon" ||
    !classic.drop_date ||
    new Date(classic.drop_date).getTime() <= Date.now()
  );

  return (
    <div data-testid="home-page">
      <div className="pt-16">
        <DropBanner />
      </div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden border-b border-wire noise-overlay" data-testid="hero-section">
        <div className="absolute inset-0 grunge-texture opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/40 to-obsidian" />
        <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 gap-12 px-5 pt-32 pb-16 sm:px-8 lg:grid-cols-12 lg:items-center lg:pt-24 lg:pb-0 lg:min-h-screen">
          <motion.div style={{ y: textY }} className="lg:col-span-7 lg:py-24">
            <Reveal delay={0.1}>
              <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] text-neutral-400" data-testid="hero-tagline">
                MORE THAN CLOTHING. IT'S AN OUTFIT.
              </p>
            </Reveal>
            <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.9] tracking-tight text-white">
              <MaskedLine delay={0.25} className="text-5xl sm:text-7xl lg:text-8xl xl:text-9xl">DEJON</MaskedLine>
              <MaskedLine delay={0.4} className="text-5xl sm:text-7xl lg:text-8xl xl:text-9xl">
                <span className="text-outline" style={{ WebkitTextStroke: "1.5px #f5f5f5" }}>RICHH</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.6}>
              <div className="mt-10 flex flex-wrap items-end gap-x-8 gap-y-4">
                <p className="font-script text-4xl sm:text-5xl text-white" data-testid="hero-price">$25</p>
                <p className="pb-2 font-mono text-[11px] tracking-[0.3em] text-neutral-400">
                  CLASSIC<br />DEJON RICHH TEE
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.75}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/product/classic-dejon-richh-tee" data-testid="hero-shop-now-button"
                  className="group flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                  SHOP NOW
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/collections" data-testid="hero-collections-button"
                  className="border border-wire px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                  COLLECTIONS
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.9}>
              <p className="mt-14 font-mono text-[10px] tracking-[0.3em] text-neutral-600">
                PREMIUM QUALITY&nbsp;&nbsp;/&nbsp;&nbsp;TIMELESS STYLE&nbsp;&nbsp;/&nbsp;&nbsp;BUILT DIFFERENT
              </p>
            </Reveal>
          </motion.div>

          <motion.div style={{ y: imgY }} className="lg:col-span-5" data-testid="hero-product-frame">
            <Reveal delay={0.5}>
              <div className="relative border border-wire bg-charcoal/60 p-3">
                <div className="pointer-events-none absolute -inset-px z-10 bg-[radial-gradient(circle_at_50%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
                <img src="/products/pair.png" alt="Classic Dejon Richh Tee — front and back view"
                  className="w-full object-cover" data-testid="hero-product-image" />
                <div className="flex items-center justify-between border-t border-wire px-2 pt-3 pb-1">
                  <p className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">CLASSIC TEE — FRONT / BACK</p>
                  <p className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">DR-001</p>
                </div>
              </div>
            </Reveal>
          </motion.div>
        </div>
      </section>

      {classicDropped && backstabber && <HolidayDrop product={backstabber} />}

      <Marquee items={["MORE THAN CLOTHING", "IT'S AN OUTFIT", "DEJON RICHH", "EST. 2026"]} />

      {/* FEATURED PRODUCT */}
      <section className="border-b border-wire" data-testid="featured-product-section">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-12">
          <div className="relative border-b border-wire lg:border-b-0 lg:border-r lg:col-span-7">
            <Reveal className="p-6 sm:p-12">
              <div className="relative overflow-hidden border border-wire bg-charcoal">
                <img src="/products/front.png" alt="Classic Dejon Richh Tee front view" className="w-full object-cover transition-transform duration-700 hover:scale-[1.03]" data-testid="featured-front-image" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                {["/products/back.png", "/products/detail.png", "/products/collar.png"].map((src, i) => (
                  <Link key={src} to="/product/classic-dejon-richh-tee" data-testid={`featured-thumb-${i}`}
                    className="overflow-hidden border border-wire bg-charcoal transition-colors hover:border-neutral-500">
                    <img src={src} alt="Product detail" className="aspect-square w-full object-cover" />
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center px-6 py-14 sm:px-12 lg:col-span-5 lg:py-24">
            <Reveal>
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">CLASSIC — DR-001</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
                Dejon Richh Tee
              </h2>
              <p className="mt-4 font-script text-3xl text-neutral-200" data-testid="featured-price">$25.00</p>
              <p className="mt-8 max-w-md font-mono text-xs leading-relaxed tracking-wider text-neutral-400">
                A premium everyday essential featuring the signature DR chest mark and Dejon Richh cross artwork on the back. Relaxed streetwear fit, vintage-inspired finish.
              </p>
              <ul className="mt-8 space-y-2">
                {VALUES.map((v) => (
                  <li key={v} className="font-mono text-[11px] tracking-[0.25em] text-neutral-400">
                    <span className="text-neutral-600">/&nbsp;&nbsp;</span>{v}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/product/classic-dejon-richh-tee" data-testid="featured-shop-button"
                  className="group flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                  SHOP THE TEE <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-[0.3em] text-neutral-600">UNISEX — SHIPS WORLDWIDE</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="border-b border-wire" data-testid="collections-preview-section">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">THE DROPS</p>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">Collections</h2>
              </div>
              <Link to="/collections" data-testid="collections-view-all"
                className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-neutral-400 transition-colors hover:text-white">
                VIEW ALL <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-wire bg-wire sm:grid-cols-2 lg:grid-cols-4">
            {COLLECTION_TILES.map((tile, i) => (
              <Reveal key={tile.name} delay={i * 0.08} className="bg-obsidian">
                {tile.live ? (
                  <Link to={tile.to} data-testid={`collection-tile-${i}`} className="group block p-6">
                    <div className="overflow-hidden border border-wire bg-charcoal">
                      <img src={tile.img} alt={tile.name} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <p className="mt-5 font-display text-sm font-bold uppercase tracking-wide text-white">{tile.name}</p>
                    <p className="mt-2 font-mono text-[10px] tracking-[0.3em] text-neutral-400">{tile.status}</p>
                  </Link>
                ) : (
                  <div className="p-6" data-testid={`collection-tile-${i}`}>
                    <div className="relative overflow-hidden border border-wire bg-charcoal">
                      <img src={tile.img} alt={tile.name} className="aspect-[4/5] w-full object-cover opacity-40 grayscale" />
                      <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.4em] text-neutral-300">LOCKED</span>
                    </div>
                    <p className="mt-5 font-display text-sm font-bold uppercase tracking-wide text-neutral-500">{tile.name}</p>
                    <p className="mt-2 font-mono text-[10px] tracking-[0.3em] text-neutral-600">{tile.status}</p>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="border-b border-wire noise-overlay" data-testid="brand-statement-section">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 sm:py-36">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">THE MANIFESTO</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-4xl font-serif text-2xl sm:text-3xl lg:text-4xl italic leading-snug text-neutral-200">
              "It's about the outfit, the details, the energy, and the way everything comes together."
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/about" data-testid="manifesto-about-link"
              className="group mt-10 inline-flex items-center gap-3 border border-wire px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-300 transition-colors hover:border-white hover:text-white">
              OUR STORY <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <SocialStrip />
      <Newsletter />
    </div>
  );
}
